import * as Sentry from '@sentry/react'

import { subscriptionApi } from '../api'
import { params, subscription } from '../utils'

const { prepareToApi } = params

const sendToValidate = async (data, step) => {
  try {
    const response = await subscriptionApi.validate(prepareToApi(data, step))

    const { status } = response

    if (status === 200) {
      return {
        success: true,
        payload: { errors: {} },
      }
    }

    if (status === 422) {
      const { errors } = await response.json()

      return {
        success: false,
        payload: { errors },
      }
    }
  } catch { }

  return { success: false, payload: { errors: [] }, error: 'UnhandledError' }
}

const tryToSubmit = async subscription => {
  try {
    let response

    if (subscription.uuid) {
      response = await subscriptionApi.update(prepareToApi(subscription))
    } else {
      response = await subscriptionApi.create(prepareToApi(subscription))
    }

    const { status: resStatus } = response
    const { job_id, errors } = await response.json()

    if ([200, 201].includes(resStatus)) {
      return {
        success: true,
        jid: job_id
      }
    }

    if (resStatus === 422) {
      return {
        success: false,
        payload: { errors },
      }
    }
  } catch { }

  return { success: false, payload: { errors: [] }, error: 'UnhandledError' }
}

const getPdf = async uuid => {
  try {
    const response = await subscriptionApi.getPdf(uuid)

    const { ok, status } = response

    if (ok && status === 200) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      let tag = document.createElement('a')

      tag.href = url
      tag.download = `comprovante-inscricao-${uuid}.pdf`

      document.body.appendChild(tag)

      tag.click()
      tag.remove()
    }
  } catch (e) {
    console.error(e)
  }
}

const get = async (cpf, cpfResponsible) => {
  try {
    const response = await subscriptionApi.get(cpf, cpfResponsible)

    const { ok, status } = response

    if (ok && status === 200) {
      const responseBody = await response.json()

      return {
        success: true,
        payload: subscription.fromApiToState(responseBody),
      }
    }

    if (status === 404) {
      return { success: false, payload: {}, notFound: true }
    }

    Sentry.captureMessage(
      `Falha ao consultar inscrição: a API respondeu ${status}`,
      'error'
    )
  } catch (e) {
    Sentry.captureException(e)
  }

  return { success: false, payload: {}, notFound: false }
}

const getSubscriptionAndEnrollment = async (cpf, birthdate) => {
  try {
    const response = await subscriptionApi.getSubscriptionAndEnrollment(cpf, birthdate)

    const { ok, status } = response

    if (ok && status === 200) {
      const responseBody = await response.json()

      return {
        success: true,
        payload: responseBody,
      }
    } else if (status === 404) {
      return {
        success: true,
        payload: {},
      }
    }
  } catch { }

  return {
    success: false,
    payload: {},
  }
}

const verifyResponsible = async (cpf, birthdate, responsibleCpf) => {
  try {
    const response = await subscriptionApi.verifyResponsible(
      cpf,
      birthdate,
      responsibleCpf
    )

    if (response.ok && response.status === 200) {
      return { success: true, payload: await response.json() }
    }
  } catch { }

  return { success: false, payload: {} }
}

const genericCandidateActionError =
  'Não foi possível concluir sua solicitação. Tente novamente mais tarde.'

const confirmInterest = async (cpf, cpfResponsible) => {
  try {
    const response = await subscriptionApi.confirmInterest(cpf, cpfResponsible)

    if (response.status === 200) {
      const { interest_confirmed_at } = await response.json()

      return {
        success: true,
        payload: { interestConfirmedAt: interest_confirmed_at },
      }
    }
  } catch {}

  return {
    success: false,
    payload: { error: genericCandidateActionError },
  }
}

const receiptFromApi = receipt => ({
  uuid: receipt.uuid,
  protocol: receipt.protocol,
  returnedAt: receipt.returned_at,
  returnedAtFormatted: receipt.returned_at_formatted,
  ip: receipt.ip,
  firstOption: {
    schoolName: receipt.first_option?.school_name,
    shiftName: receipt.first_option?.shift_name,
    curriculumName: receipt.first_option?.curriculum_name,
    degreeName: receipt.first_option?.degree_name,
    levelName: receipt.first_option?.level_name,
    cityName: receipt.first_option?.city_name,
  },
})

const returnToWaitingList = async (cpf, cpfResponsible) => {
  try {
    const response = await subscriptionApi.returnToWaitingList(
      cpf,
      cpfResponsible
    )

    if ([200, 201].includes(response.status)) {
      const receipt = await response.json()

      return {
        success: true,
        payload: { receipt: receiptFromApi(receipt) },
      }
    }
  } catch {}

  return {
    success: false,
    payload: { error: genericCandidateActionError },
  }
}

const getReturnReceiptPdf = async (cpf, cpfResponsible, uuid) => {
  try {
    const response = await subscriptionApi.getReturnReceiptPdf(
      cpf,
      cpfResponsible
    )

    const { ok, status } = response

    if (ok && status === 200) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      let tag = document.createElement('a')

      tag.href = url
      tag.download = `comprovante-retorno-suplencia-${uuid}.pdf`

      document.body.appendChild(tag)

      tag.click()
      tag.remove()
    }
  } catch (e) {
    console.error(e)
  }
}

const jobStatus = async (jid, jobAction) => {
  try {
    const response = await subscriptionApi.jobStatus(jid, jobAction)

    const { status } = response
    const responseBody = await response.json()

    return {
      success: status === 200 || status === 202,
      status: status,
      payload: responseBody?.data?.attributes,
      message: responseBody?.message,
      error: responseBody?.error,
    }

  } catch (error) {

    return {
      success: false,
      status: 500,
      error: 'Erro interno do servidor',
      message: 'Servidor temporariamente indisponível. Tente novamente mais tarde.',
    }
  }
}

const subscriptionService = {
  get,
  getPdf,
  sendToValidate,
  jobStatus,
  tryToSubmit,
  getSubscriptionAndEnrollment,
  verifyResponsible,
  confirmInterest,
  returnToWaitingList,
  getReturnReceiptPdf,
}

export default subscriptionService
